import { useCallback, useEffect, useState } from 'react';

export const TAILLES_PAGE_DISPONIBLES = [10, 15, 25, 50, 100] as const;
export type TaillePage = (typeof TAILLES_PAGE_DISPONIBLES)[number];

const CLE_STOCKAGE = 'top_formation_preference_taille_page';
export const TAILLE_PAGE_PAR_DEFAUT: TaillePage = 25;

/**
 * Lit la préférence de pagination enregistrée dans le navigateur.
 * Renvoie 25 par défaut si aucune valeur valide n'est stockée.
 */
export function lirePreferenceTaillePage(): TaillePage {
  if (typeof window === 'undefined') return TAILLE_PAGE_PAR_DEFAUT;
  try {
    const stocke = window.localStorage.getItem(CLE_STOCKAGE);
    if (!stocke) return TAILLE_PAGE_PAR_DEFAUT;
    const nombre = parseInt(stocke, 10);
    if (TAILLES_PAGE_DISPONIBLES.includes(nombre as TaillePage)) {
      return nombre as TaillePage;
    }
  } catch {
    // Mode navigation privée stricte ou stockage désactivé
  }
  return TAILLE_PAGE_PAR_DEFAUT;
}

/**
 * Enregistre la préférence utilisateur dans le localStorage et
 * notifie tous les composants abonnés de l'application.
 */
export function enregistrerPreferenceTaillePage(taille: TaillePage): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CLE_STOCKAGE, String(taille));
    window.dispatchEvent(
      new CustomEvent<TaillePage>('top_formation_taille_page_change', { detail: taille }),
    );
  } catch {
    // Stockage inaccessible
  }
}

/**
 * Hook React retournant la taille de pagination préférée de l'utilisateur
 * (25, 50 ou 100) et la fonction de modification réactive et persistante.
 */
export function useTaillePage(): [TaillePage, (nouvelleTaille: TaillePage) => void] {
  const [taille, setTaille] = useState<TaillePage>(lirePreferenceTaillePage);

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === CLE_STOCKAGE && e.newValue) {
        const val = parseInt(e.newValue, 10);
        if (TAILLES_PAGE_DISPONIBLES.includes(val as TaillePage)) {
          setTaille(val as TaillePage);
        }
      }
    }

    function onCustom(e: Event) {
      const customEvent = e as CustomEvent<TaillePage>;
      if (
        customEvent.detail &&
        TAILLES_PAGE_DISPONIBLES.includes(customEvent.detail)
      ) {
        setTaille(customEvent.detail);
      }
    }

    window.addEventListener('storage', onStorage);
    window.addEventListener('top_formation_taille_page_change', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('top_formation_taille_page_change', onCustom);
    };
  }, []);

  const changerTaille = useCallback((nouvelleTaille: TaillePage) => {
    setTaille(nouvelleTaille);
    enregistrerPreferenceTaillePage(nouvelleTaille);
  }, []);

  return [taille, changerTaille];
}
